import React, { useEffect, useState } from "react";
import api from "../api/client";
import { useTheme } from "../context/ThemeContext";
import { isStandaloneDisplay, subscribeInstallable, triggerInstall } from "../pwa";
import { useBranding } from "../context/BrandingContext";
import { browserTimeZone, formatDateTime, supportedTimeZones } from "../utils/datetime";

export default function Settings() {
  const branding = useBranding();
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/api/v1/settings").then((r) => setSettings(r.data));
  }, []);

  // Live authoritative appliance clock — the source every timestamp is stamped
  // from. Poll on mount + every 30s so an operator can verify accuracy.
  const [srvTime, setSrvTime] = useState<{
    utc: string;
    display_timezone: string;
    display_timezone_valid: boolean;
    local: string;
  } | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get("/api/v1/settings/server-time")
        .then((r) => alive && setSrvTime(r.data))
        .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Alert thresholds + offline policy moved to Monitoring & Alerts → Settings
  // (pages/MonitoringSettings.tsx).

  // SLA policy moved to HelpDesk -> Settings.

  // Patch approval policy moved to Devices → Patch Compliance
  // (components/PatchApprovalPolicyCard.tsx), 2026-07-07.
  const save = async (key: string, value: string) => {
    setSaving(key);
    await api.put(`/api/v1/settings/${key}`, { value });
    setSettings((s) => ({ ...s, [key]: value }));
    setSaving(null);
    setMsg(`Saved ${key}`);
    setTimeout(() => setMsg(""), 3000);
  };

  // Timezone save also refreshes branding so every timestamp in the app
  // (which reads the zone from BrandingContext) re-renders immediately.
  const saveTimezone = async (tz: string) => {
    setSaving("display_timezone");
    await api.put("/api/v1/settings/display_timezone", { value: tz });
    setSettings((s) => ({ ...s, display_timezone: tz }));
    await branding.refresh();
    setSaving(null);
    setMsg("Saved display time zone");
    setTimeout(() => setMsg(""), 3000);
  };



  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Settings</h1>
      {msg && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-sm px-4 py-2 rounded-lg">{msg}</div>
      )}

      <div className="space-y-6">
        <AppearanceCard />

        {/* The Branding card was removed from this page on request. The
            underlying app_settings rows (email_brand_name, brand_color,
            support_email, tagline, the console/portal eyebrows and the
            uploaded logo) are UNTOUCHED and still drive the login screen,
            the customer portal and every email template - only the editor
            here is gone. Restore this card, or move it, by reading those
            same keys via PUT /api/v1/settings/<key>. */}
        {/* Date & Time */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Date &amp; Time</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Every timestamp in the product is stamped from the appliance clock (UTC) and
            displayed in the zone below. If the appliance clock reads correctly here, all
            timestamps are accurate — regardless of the viewer's browser zone.
          </p>

          {/* Live appliance clock — the accuracy check */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Appliance time ({srvTime?.display_timezone?.replace(/_/g, " ") || "—"})
              </div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">
                {srvTime ? srvTime.local : "…"}
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Appliance time (UTC)
              </div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">
                {srvTime ? formatDateTime(srvTime.utc, "UTC") : "…"}
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Your browser
              </div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">
                {browserTimeZone()}
              </div>
            </div>
          </div>
          {srvTime && !srvTime.display_timezone_valid && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
              The configured zone "{srvTime.display_timezone}" isn't a valid IANA name; showing UTC.
              Pick a zone below.
            </p>
          )}

          <div className="flex items-start justify-between py-4 border-t border-gray-100 dark:border-gray-700 last:border-0">
            <div className="flex-1 pr-8">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Display Time Zone</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                The zone all absolute timestamps are shown in (last-login, tickets, alerts, audit
                log, and the customer portal). "Last seen" / "last polled" show precise relative
                time and don't depend on this. Saved immediately.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={settings.display_timezone || branding.display_timezone || "UTC"}
                onChange={(e) => saveTimezone(e.target.value)}
                disabled={saving === "display_timezone"}
                className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {supportedTimeZones().map((z) => (
                  <option key={z} value={z}>
                    {z.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* The Agent Files card (GoToAssist + Huntress installer uploads) was
            removed from this page on request. The upload endpoints
            /api/v1/settings/gotoassist-installer and .../huntress-installer
            still exist and any already-uploaded installer is still on disk and
            still auto-deploys - this only drops the upload UI. */}
        {/* The global SLA policy moved to HelpDesk -> Settings
            (pages/HelpDeskSettings.tsx). It is queue policy a helpdesk manager
            tunes, not appliance admin, and here it sat between installer
            uploads and auth policy. */}

        {/* Agent build/rollout management intentionally omitted from the
            resellable product — agents are built + registered + rolled out by
            the appliance/vendor, not the MSP operator. */}

        {/* Monitoring settings (thresholds, offline policy/timers, alert email)
            now live under Monitoring & Alerts → Settings. */}

        {/* Authentication policy */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Authentication policy</h2>
          <label className="flex items-start gap-2 mt-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={settings.require_mfa === "true"}
              disabled={saving === "require_mfa"}
              onChange={(e) => save("require_mfa", e.target.checked ? "true" : "false")}
            />
            <span>
              <span className="font-medium">Require MFA for all users</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Every user without MFA is routed to the Security page to enroll before they can use
                the app. On by default for new appliances (production posture).
              </span>
            </span>
          </label>
        </div>

        <SetupCard />

        <RecoveryBundleCard />

        <FactoryResetCard />
      </div>
    </div>
  );
}

function FactoryResetCard() {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reset = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post("/api/v1/setup/factory-reset", { confirm: "RESET" });
      // All data (including this admin account) is gone; drop the now-invalid
      // token and reload — the appliance comes back at the first-run wizard.
      localStorage.removeItem("token");
      window.location.href = "/";
    } catch (e: unknown) {
      const x = e as { response?: { data?: { detail?: string } } };
      setErr(x.response?.data?.detail || "Factory reset failed. No data was changed.");
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-200 dark:border-red-900/50 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-1">Reset to factory defaults</h2>
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
        Permanently erase <span className="font-medium">all data</span> on this appliance — every
        customer, device, ticket, alert, integration, user account, and setting — and return it to
        the out-of-box first-run experience. The database schema and the master encryption keys are
        kept so the appliance still boots cleanly into the setup wizard.{" "}
        <span className="font-medium text-red-700 dark:text-red-400">This cannot be undone.</span> Export a recovery
        bundle and any reports you need first.
      </p>
      <button
        onClick={() => {
          setPhrase("");
          setErr("");
          setOpen(true);
        }}
        className="px-4 py-1.5 text-sm border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30"
      >
        Reset to factory defaults…
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Confirm factory reset</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This will permanently delete every customer, device, ticket, alert, user account, and
              setting on this appliance and return it to the first-run setup wizard. You will be
              signed out and must create a new administrator account. This action is irreversible.
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
              To confirm, type <span className="font-mono font-semibold">RESET</span> below.
            </p>
            <input
              autoFocus
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder="RESET"
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-red-300"
            />
            {err && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{err}</p>}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                disabled={busy}
                className="px-4 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={reset}
                disabled={busy || phrase !== "RESET"}
                className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? "Resetting…" : "Erase all data & reset"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AppearanceCard() {
  const { theme, toggle } = useTheme();
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    if (isStandaloneDisplay()) return;
    return subscribeInstallable(setCanInstall);
  }, []);

  // Was "Account & appearance", a catch-all that mixed a display preference
  // with links to other pages: Security & MFA, Change password and Help.
  // Those all have their own homes in the sidebar now (Admin -> Security & MFA,
  // and Help at the foot of the menu), so duplicating them here just made this
  // page the place people went to leave it. Only the theme toggle is a genuine
  // setting, so that is all that remains.
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">Theme</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Switch between light and dark mode.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canInstall && (
            <button
              onClick={() => triggerInstall()}
              className="text-sm border border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 rounded-lg px-3 py-1.5 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
              title="Install this console as an app on this device"
            >
              ⬇ Install app
            </button>
          )}
          <button
            onClick={toggle}
            className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            {theme === "dark" ? "☀ Switch to light" : "☾ Switch to dark"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SetupCard() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reopen = async () => {
    if (
      !window.confirm(
        "Re-open the initial setup wizard? Your existing settings are preserved and " +
          "pre-filled — this just lets you complete or change steps you skipped.",
      )
    )
      return;
    setBusy(true);
    setErr("");
    try {
      await api.post("/api/v1/setup/reopen");
      // The SPA shows the wizard whenever setup isn't marked complete; a full
      // reload re-checks /setup/status and routes into it.
      window.location.href = "/";
    } catch (e: unknown) {
      const x = e as { response?: { data?: { detail?: string } } };
      setErr(x.response?.data?.detail || "Couldn't re-open the setup wizard.");
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Initial setup wizard</h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Re-run the first-run wizard to complete or revisit any step you skipped (company info,
        email, license, etc.). Your current settings are preserved and pre-filled — nothing is
        wiped, and the admin-account step is skipped since an admin already exists.
      </p>
      {err && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{err}</p>}
      <button
        onClick={reopen}
        disabled={busy}
        className="px-4 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
      >
        {busy ? "Opening…" : "Re-run setup wizard"}
      </button>
    </div>
  );
}

function RecoveryBundleCard() {
  const [passphrase, setPassphrase] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setMsg("");
    setBusy(true);
    try {
      const r = await api.post(
        "/api/v1/settings/recovery-bundle",
        { passphrase },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "aerogauge-recovery.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      setMsg("Downloaded. Store this file + passphrase somewhere safe and offline.");
      setPassphrase("");
    } catch (e: unknown) {
      // Blob error bodies need decoding to read the detail.
      const ex = e as { response?: { data?: Blob | { detail?: string } } };
      let detail = "Export failed";
      const data = ex.response?.data;
      if (data instanceof Blob) {
        try {
          detail = JSON.parse(await data.text())?.detail || detail;
        } catch {
          /* keep default */
        }
      } else if (data && typeof data === "object") {
        detail = data.detail || detail;
      }
      setMsg(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Recovery bundle</h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Export this appliance's master encryption keys as a passphrase-encrypted file. Without it,
        encrypted data (customer credentials, AI keys, BitLocker escrow) is unrecoverable if the
        appliance is lost. Store the file and passphrase separately.
      </p>
      <div className="flex items-end gap-2">
        <label className="text-xs flex-1">
          <span className="block text-gray-600 dark:text-gray-400 mb-1">Passphrase (min 12 chars)</span>
          <input
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
          />
        </label>
        <button
          onClick={download}
          disabled={busy || passphrase.length < 12}
          className="px-4 py-1.5 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
        >
          {busy ? "Exporting…" : "Download"}
        </button>
      </div>
      {msg && <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">{msg}</p>}
    </div>
  );
}
